import { useEffect, useMemo, useState } from "react";
import { CircleDollarSign } from "lucide-react";
import api from "../lib/api";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { fmtDateTime } from "../lib/format";

export default function CostPage() {
  const [agg, setAgg] = useState(null);
  const [events, setEvents] = useState([]);
  const [missions, setMissions] = useState([]);

  useEffect(() => {
    (async () => {
      const [{ data: a }, { data: e }, { data: m }] = await Promise.all([
        api.get("/cost_events/aggregate"),
        api.get("/cost_events?limit=100"),
        api.get("/missions"),
      ]);
      setAgg(a);
      setEvents(e);
      setMissions(m);
    })();
  }, []);

  const totalUsd = agg?.total?.usd ?? 0;
  const totalIn = agg?.total?.tokens_in ?? 0;
  const totalOut = agg?.total?.tokens_out ?? 0;

  const missionTitleFor = (id) => missions.find((m) => m.id === id)?.title || id.slice(0, 8);

  const providerRows = useMemo(() => {
    const p = agg?.by_provider || {};
    return Object.entries(p)
      .map(([k, v]) => ({ key: k, ...v }))
      .sort((a, b) => (b.usd || 0) - (a.usd || 0));
  }, [agg]);

  const missionRows = useMemo(() => {
    const m = agg?.by_mission || {};
    return Object.entries(m)
      .map(([k, v]) => ({ key: k, ...v }))
      .sort((a, b) => (b.usd || 0) - (a.usd || 0));
  }, [agg]);

  const departmentRows = useMemo(() => {
    const d = agg?.by_department || {};
    return Object.entries(d)
      .map(([k, v]) => ({ key: k, ...v }))
      .sort((a, b) => (b.usd || 0) - (a.usd || 0));
  }, [agg]);

  return (
    <>
      <TopBar title="Cost Center" subtitle="AI economics per mission, department and provider." />

      <div className="kpi-grid">
        <div className="kpi blue">
          <div className="label">Total spend</div>
          <div className="value" data-testid="cost-total-usd">${totalUsd.toFixed(4)}</div>
          <div className="trend">Recorded execution cost</div>
        </div>
        <div className="kpi cyan">
          <div className="label">Input tokens</div>
          <div className="value" data-testid="cost-tokens-in">{totalIn.toLocaleString()}</div>
          <div className="trend">Across all providers</div>
        </div>
        <div className="kpi purple">
          <div className="label">Output tokens</div>
          <div className="value" data-testid="cost-tokens-out">{totalOut.toLocaleString()}</div>
          <div className="trend">Across all providers</div>
        </div>
        <div className="kpi green">
          <div className="label">Events</div>
          <div className="value" data-testid="cost-event-count">{agg?.event_count ?? 0}</div>
          <div className="trend">Cost records</div>
        </div>
      </div>

      <div className="grid-3">
        <div className="panel" data-testid="cost-by-department">
          <div className="panel-head">
            <div><h2>By department</h2><p>Where the spend concentrates.</p></div>
          </div>
          <div className="panel-body">
            {departmentRows.length === 0 ? <EmptyState title="No department data yet." /> :
              departmentRows.map((r) => (
                <div className="wide-row" key={r.key}>
                  <div>
                    <b>{r.key}</b>
                    <p>{r.tokens_in.toLocaleString()} in · {r.tokens_out.toLocaleString()} out</p>
                  </div>
                  <b className="mono">${r.usd.toFixed(4)}</b>
                </div>
              ))
            }
          </div>
        </div>

        <div className="panel" data-testid="cost-by-mission">
          <div className="panel-head">
            <div><h2>By mission</h2><p>Highest-spend first.</p></div>
          </div>
          <div className="panel-body">
            {missionRows.length === 0 ? <EmptyState title="No mission data yet." /> :
              missionRows.slice(0, 10).map((r) => (
                <div className="wide-row" key={r.key}>
                  <div>
                    <b>{missionTitleFor(r.key)}</b>
                    <p className="mono" style={{ fontSize: 11 }}>#{r.key.slice(0, 8)} · {r.tokens_in} in · {r.tokens_out} out</p>
                  </div>
                  <b className="mono">${r.usd.toFixed(4)}</b>
                </div>
              ))
            }
          </div>
        </div>

        <div className="panel" data-testid="cost-by-provider">
          <div className="panel-head">
            <div><h2>By provider</h2><p>Adapter-level spend.</p></div>
          </div>
          <div className="panel-body">
            {providerRows.length === 0 ? <EmptyState title="No provider data yet." /> :
              providerRows.map((r) => (
                <div className="wide-row" key={r.key}>
                  <div>
                    <b>{r.key}</b>
                    <p>{r.tokens_in.toLocaleString()} in · {r.tokens_out.toLocaleString()} out</p>
                  </div>
                  <b className="mono">${r.usd.toFixed(4)}</b>
                </div>
              ))
            }
          </div>
        </div>
      </div>

      <div className="panel" style={{ marginTop: 14 }}>
        <div className="panel-head">
          <div><h2>Recent events</h2><p>Newest first · one row per LLM run.</p></div>
          <span className="pill dim">{events.length}</span>
        </div>
        <div className="panel-body" style={{ maxHeight: 460, overflow: "auto" }}>
          {events.length === 0 ? (
            <EmptyState title="No cost events yet." icon={CircleDollarSign} />
          ) : (
            <table className="ledger-table" data-testid="cost-events-table">
              <thead>
                <tr><th>TS</th><th>PROVIDER · MODEL</th><th>DEPT</th><th>TOKENS</th><th>USD</th></tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id}>
                    <td className="hint">{fmtDateTime(e.ts)}</td>
                    <td className="mono">{e.provider} · {e.model}</td>
                    <td>{e.department_key || "—"}</td>
                    <td className="mono">{e.tokens_in}→{e.tokens_out}</td>
                    <td className="mono">${e.usd_estimate.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
